// Front-nine / back-nine play. Pure functions, no imports outside domain types.
//
// The whole app indexes `round.holes[i]` and `tee.holes[i]` in parallel, so the way to
// support a nine is to hand every consumer a tee whose `holes` array IS the nine being
// played. Slice once at the edge (round setup, buildCtx) and nothing downstream — engines,
// parFor, the scorecard — needs to know a nine is happening.

import type { Course, HoleSet, Tee } from '../types';

/** Index of the first played hole within the tee's full hole array. */
export function holeSetStart(holeSet: HoleSet, total: number): number {
  return holeSet === 'back' ? Math.ceil(total / 2) : 0;
}

/** How many holes this set plays. */
export function holeSetCount(holeSet: HoleSet, total: number): number {
  if (holeSet === 'full') return total;
  return holeSet === 'back' ? total - Math.ceil(total / 2) : Math.ceil(total / 2);
}

/** Take the played slice out of any per-hole array (holes, scores, whatever). */
export function sliceForHoleSet<T>(arr: T[], holeSet: HoleSet): T[] {
  if (holeSet === 'full') return arr;
  const start = holeSetStart(holeSet, arr.length);
  return arr.slice(start, start + holeSetCount(holeSet, arr.length));
}

/**
 * Slice a tee to the played nine, re-ranking stroke indexes to 1..n.
 *
 * Re-ranking matters: a back nine's stroke indexes are typically 2,4,6…18, but
 * `allocateStrokes` expects 1..n and would allocate nonsense against the raw numbers.
 * Ranking preserves the relative difficulty order, so the hardest hole of the nine still
 * takes the first stroke — which is how a nine is played.
 */
export function sliceTee(tee: Tee, holeSet: HoleSet): Tee {
  if (holeSet === 'full') return tee;
  const holes = sliceForHoleSet(tee.holes, holeSet);
  const order = [...holes].sort((a, b) => a.strokeIndex - b.strokeIndex);
  const rankOf = new Map(order.map((h, i) => [h.strokeIndex, i + 1]));
  return {
    ...tee,
    holes: holes.map((h) => ({ ...h, strokeIndex: rankOf.get(h.strokeIndex) ?? h.strokeIndex })),
  };
}

export function sliceCourse(course: Course, holeSet: HoleSet): Course {
  if (holeSet === 'full') return course;
  return { ...course, tees: course.tees.map((t) => sliceTee(t, holeSet)) };
}

/**
 * The tee a player is actually scoring against: sliced to the played nine, so `tee.holes[i]`
 * lines up with `round.holes[i]`. Falls back to the first tee of the played course.
 *
 * A TestFlight screenshot from a back-nine round at Bonnie Brook showed the header reading
 * "Par 4" for hole 10 while the player row still treated it as hole 1's par 5 (a gross 4 got
 * a birdie circle). That happened because the row looked up the tee on the unsliced course
 * at the round-relative index. Reading through this helper instead of `course.tees.find(...)`
 * keeps every consumer on the same sliced tee as the header.
 */
export function playedTeeFor(
  course: Course,
  holeSet: HoleSet | undefined,
  teeId: string | undefined
): Tee | undefined {
  const played = sliceCourse(course, holeSet ?? 'full');
  return played.tees.find((t) => t.id === teeId) ?? played.tees[0];
}

/**
 * A nine plays half the strokes, so it plays half the handicap.
 *
 * A strict treatment wants a separate 9-hole rating and slope; published course data here carries
 * only the 18-hole pair, so halving the 18-hole course handicap is both the accurate option
 * given the data and what groups actually do on the first tee.
 */
export function courseHandicapForHoleSet(ch18: number, holeSet: HoleSet): number {
  return holeSet === 'full' ? ch18 : Math.round(ch18 / 2);
}

/** How much to add to a played-hole index to get its real hole number (back nine → 9). */
export function holeNumberOffset(course: Course, holeSet: HoleSet): number {
  return holeSetStart(holeSet, course.tees[0]?.holes.length ?? 18);
}

export const HOLE_SET_LABEL: Record<HoleSet, string> = {
  full: '18 holes',
  front: 'Front 9',
  back: 'Back 9',
};

/**
 * The same labels, told how long the course actually is.
 *
 * `HOLE_SET_LABEL` hard-codes eighteen, so a NINE-hole course has always offered its golfer a
 * chip reading "18 holes" — wrong before short courses existed and wronger now that a course
 * can have five. Front and back are halves rather than nines: `holeSetCount` already splits on
 * `Math.ceil(total / 2)`, so on a 9-hole course the front is 5 holes and the back is 4, and
 * the label should say so instead of promising nine of each.
 */
export function holeSetLabel(holeSet: HoleSet, totalHoles: number): string {
  if (holeSet === 'full') return `${totalHoles} holes`;
  const n = holeSetCount(holeSet, totalHoles);
  const half = holeSet === 'front' ? 'Front' : 'Back';
  return `${half} ${n}`;
}
